import { Controller } from '@hotwired/stimulus'
import { debounce, flattenDepth } from 'lodash'
import Address from '../../models/address'
import { removeAllChildren } from '../../shared/dom';
import { flashAlert } from '../../shared/flash';

import $ from 'jquery';
import 'select2';

export default class extends Controller {
  static targets = [
    'address',
    'container',
    'deliveryAddressOptions',
    'deliveryButton',
    'deliveryContainer',
    'destinationButton',
    'destinationContainer',
    'destinationSelect',
    'productSelect'
  ];

  loadGoogleMaps() {
    // NOTE: CHECK BEFORE EVERY CALL TO this.maps/autocomplete/geocoder
    // Google Maps is defined on window in callback from script tag in booking.haml.
    // Not guaranteed to be loaded at connect, so we need to call this to ensure definition.
    if (!window.GoogleMaps) {
      return false
    }

    this.googleMaps ||= window.GoogleMaps
    this.autocompleteService ||= new this.googleMaps.places.AutocompleteService()
    this.geocoder ||= new this.googleMaps.Geocoder()

    return true
  }

  initialize() {
    this.pageRequestSize = 25;
    this.fetchHeaders = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    }
    this.addressInput = debounce(this.addressInput, 200)
    this.products = [];
  }

  connect() {
    this.loadGoogleMaps()
    this.ownerId = this.containerTarget.dataset.ownerId;
    this.isDeliveryOrder = this.containerTarget.hasAttribute('data-delivery')
    this.destinationId = this.containerTarget.dataset?.orderDestinationId
    this.$destinationSelect = $('#destination-select')
    this.$destinationSelect.select2({ placeholder: 'Destination Name', disabled: !!this.destinationId })
    this.$destinationSelect.on('select2:select', event => this.onDestinationSelect(event))
    this.$destinationSelect.on('select2:open', () => this.focusSelect2SearchField())

    if (this.destinationId) {
      this.setDestinationId(this.destinationId)
      this.disableDestinationChange()
    } else {
      this.getOwnerServiceAreas()
    }

    this.$productSelect = $('#product-select');
    this.$productSelect.select2({ placeholder: 'Product Name' });
    this.$productSelect.on('select2:open', () => this.focusSelect2SearchField());

    document.addEventListener('orderLoaded', this.onOrderLoad)
    document.addEventListener('cartAddSuccess', this.onCartAddSuccess, true)
  }

  disableDestinationChange() {
    this.$destinationSelect.prop('disabled', true)
    this.deliveryButtonTarget.classList.add('hidden')
    this.destinationButtonTarget.classList.add('hidden')

    if (this.isDeliveryOrder) {
      this.deliveryContainerTarget.classList.remove('hidden')
      this.destinationContainerTarget.classList.add('hidden')
    } else {
      this.destinationContainerTarget.classList.remove('hidden')
      this.deliveryContainerTarget.classList.add('hidden')
    }
  }

  startDelivery() {
    this.destinationId = null
    this.$productSelect.val(null).trigger('change')
    this.$productSelect.empty()
    this.deliveryButtonTarget.classList.add('hidden')
    this.destinationContainerTarget.classList.add('hidden')
    this.destinationButtonTarget.classList.remove('hidden')
    this.deliveryContainerTarget.classList.remove('hidden')
    this.isDeliveryOrder = true
    this.$destinationSelect.val(null)
    this.onDestinationSelect(null)
    this.addressTarget.focus()
  }

  startDestinationSelect() {
    this.destinationId = null
    this.$productSelect.val(null).trigger('change')
    this.addressTarget.value = ''

    this.destinationButtonTarget.classList.add('hidden')
    this.deliveryContainerTarget.classList.add('hidden')
    this.deliveryButtonTarget.classList.remove('hidden')
    this.destinationContainerTarget.classList.remove('hidden')

    this.isDeliveryOrder = false
    this.addressTarget.value = null
  }

  addressInput() {
    this.updateAddressOptions(this.addressTarget.value)
  }

  updateAddressOptions(input) {
    if (!this.loadGoogleMaps()) {
      return
    }

    const bounds = this.autoCompleteBounds
    const options = { bounds, strictBounds: false }
    this.autocompleteService.getPlacePredictions({ input, options }, (predictions, status) => {
      if (status === 'OK') {
        removeAllChildren(this.deliveryAddressOptionsTarget)
        predictions.forEach(prediction => {
          const option = new Option(prediction.description, prediction.description)
          option.setAttribute('data-place-id', prediction.place_id)
          this.deliveryAddressOptionsTarget.append(option)
        })
      } else {
        console.error('Problem getting Google Maps address predictions', status, predictions)
      }
    })
  }

  finalizeAddress() {
    if (!this.loadGoogleMaps()) {
      return
    }

    const address = this.addressTarget.value
    const optionMatch = Array.prototype.find.call(this.deliveryAddressOptionsTarget.childNodes, el => el.value === address)
    const placeId = optionMatch?.dataset?.placeId
    if (!placeId && (!address || address.length < 5)) {
      return
    }

    let req = {} // Google wants placeId or address, but not both. Use more accurate placeId if available
    if (placeId) {
      req.placeId = placeId
    } else {
      req.address = address
    }

    this.geocoder.geocode(req, (results, status) => {
      if (status !== 'OK') {
        console.error('Problem getting Google Maps address predictions', { status }, { results })
        return
      }

      if (results[0]) {
        const result = results[0]
        const loc = result.geometry.location
        const deliveryAddress = Address.fromGoogleAddress(result)

        if (deliveryAddress.address1) {
          this.deliveryAddress = deliveryAddress
          this.getServiceAreaByLatLng(loc.lat(), loc.lng())
        } else {
          flashAlert('Please select delivery address with number & street information')
        }
      }
    })
  }

  setDeliveryAddress(address) {
    if (address) {
      this.deliveryAddress = address
      this.addressTarget.value = address.singleLineFormat
    } else {
      this.addressTarget.value = ''
    }

    if (this.isDeliveryOrder) {
      this.addressTarget.disabled = true
      this.$destinationSelect.prop('disabled', true)
    } else {
      this.addressTarget.disabled = false
      this.$destinationSelect.prop('disabled', false)
    }
  }

  setDestinationId(destinationId) {
    this.destinationId = destinationId
    this.$destinationSelect.val(destinationId).trigger('change')
    this.requestProducts()
    const detail = { destinationId: this.destinationId, deliveryAddress: this.deliveryAddress }
    const destinationSelectEvent = new CustomEvent('destinationSelect', { detail })
    document.dispatchEvent(destinationSelectEvent)
  }

  getServiceAreaByLatLng(lat, lng) {
    if (!this.ownerId) {
      return [];
    }
    fetch(
      `/sandbar/admin/owners/${this.ownerId}/service_areas.json?lat=${lat}&lng=${lng}&active=${true}`,
      {
        method: 'GET',
        headers: this.fetchHeaders,
      }
    )
      .then(resp => {
        if (resp.ok) {
          resp.json().then(serviceAreas => {

            if (serviceAreas.length) {
              this.serviceArea = serviceAreas[0]
              this.setDestinationId(this.serviceArea.delivery_warehouse_id);
            } else {
              flashAlert("Sorry, it looks like that address isn't in range for delivery.")
            }
          })
        } else {
          resp.json().then(err => this.handleError(err))
        }
      })
      .catch(err => this.handleError(err));
  }

  getOwnerServiceAreas() {
    this.loadGoogleMaps()
    if (!this.ownerId) {
      return [];
    }
    fetch(
      `/sandbar/admin/owners/${this.ownerId}/service_areas.json?active=${true}`,
      {
        method: 'GET',
        headers: this.fetchHeaders,
      }
    )
      .then(resp => {
        if (resp.ok) {
          resp.json().then(serviceAreas => {
            this.autoCompleteBounds = this.mapsBounds(serviceAreas)
          })
        } else {
          resp.json().then(err => this.handleError(err))
        }
      })
      .catch(err => this.handleError(err));
  }

  mapsBounds(serviceAreas) {
    if (!this.googleMaps) {
      this.loadGoogleMaps()
      return
    }

    // rgeo data containing type ('Polygon') + an array of coordinate arrays (can have 'holes')
    const geographies = serviceAreas.map(sa => sa.geog)
    // collect [lat, lng] pairs from each coordinate set for each service area
    const coordinates = flattenDepth(geographies.map(geog => geog.coordinates), 2)
    const lats = coordinates.map(pair => pair[0])
    const lngs = coordinates.map(pair => pair[1])
    const sw = new this.googleMaps.LatLng(Math.min(...lngs), Math.min(...lats))
    const ne = new this.googleMaps.LatLng(Math.max(...lngs), Math.max(...lats))
    return new this.googleMaps.LatLngBounds(sw, ne)
  }

  onOrderLoad = event => {
    const order = event?.detail?.order;
    this.orderId = order?.id;
    this.destinationId = order?.destination_id

    if (this.destinationId) {
      this.setDestinationId(this.destinationId)
      this.disableDestinationChange()
    } else {
      this.$destinationSelect.prop('disabled', false)
    }

    this.isDeliveryOrder = order?.delivery

    if (this.isDeliveryOrder) {
      const address = Address.fromAttributes(order?.delivery_address)
      this.setDeliveryAddress(address)
    }
  }

  onCartAddSuccess = ({ detail }) => {
    this.destinationId = detail?.destinationId
    if (this.destinationId) {
      this.setDestinationId(this.destinationId)
      this.disableDestinationChange()
      this.containerTarget.dataset.destinationId = this.destinationId
    }
    this.$productSelect.val(null).trigger('change')
  }

  onDestinationSelect = _event => {
    const inputValue = this.$destinationSelect.val()
    const id = inputValue ? parseInt(this.$destinationSelect.val()) : null
    this.setDestinationId(id)
  }

  onProductSelect = event => {
    const selectedId = parseInt(event.currentTarget.value);
    for (let i = 0; i < this.products.length; i += 1) {
      if (this.products[i].id === selectedId) {
        this.product = this.products[i];
        break;
      }
    }

    this.destinationId ||= this.$destinationSelect.val()
    let productSelectEvent = new CustomEvent('productSelect', { detail: { product: this.product, destinationId: this.destinationId } })
    document.dispatchEvent(productSelectEvent)
  }

  requestProducts() {
    if (!this.destinationId) {
      return [];
    }

    fetch(`/sandbar/admin/admin_products?destination_id=${this.destinationId}`)
      .then(resp => {
        if (resp.ok) {
          return resp.json()
        } else {
          resp.json().then(err => this.handleError(err))
        }
      })
      .then(products => {
        if (this.isDeliveryOrder) {
          products = products.filter(product => product.is_delivery)
        }
        this.products = products.map(product => {
          return {
            ...product,
            text: product.name,
            data: { is_rental: product.is_rental },
          };
        });

        this.initializeProductSelect();

        const detail = { products: this.products }
        const productsLoadedEvent = new CustomEvent('productsLoaded', { detail })
        document.dispatchEvent(productsLoadedEvent)
      })
  }

  initializeProductSelect = () => {
    this.$productSelect.empty();
    this.$productSelect.attr('disabled', true);

    this.$productSelect.select2({
      placeholder: 'Product Name',
      data: [{ id: '', text: '' }].concat(this.products)
    });
    this.$productSelect.attr('disabled', false);
    this.$productSelect.on('select2:select', e => this.onProductSelect(e))
  }

  handleError(error) {
    console.error(error)
  }

  focusSelect2SearchField() {
    setTimeout(() => {
      document.querySelector('.select2-dropdown input.select2-search__field').focus()
    }, 5);
  }
}
